
import React, { useEffect, useState, useMemo } from 'react';
import { MdRefresh, MdCancel, MdDelete, MdCalendarToday, MdAccessTime, MdLocationOn, MdBusiness, MdCheckCircle, MdBlock, MdPending, MdBookmarkBorder } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import './MyBookings.css';

// Displays user's workspace bookings
function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, upcoming, pending, cancelled
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]); // For bulk delete
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchMyBookings();

    // Auto-refresh when window/tab regains focus
    const handleFocus = () => {
      console.log('Window focused, refreshing bookings...');
      fetchMyBookings();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

 //fetching user bookings from backend
  const fetchMyBookings = async () => {
    try {
      setIsLoading(true);
      const response = await conferenceRoomApi.getMyBookings();
      console.log('RAW API RESPONSE:', response);
      console.log('Response type:', typeof response);
      console.log('Is array?', Array.isArray(response));

      // adminapi.js already returns response.data, so response is the array directly
      const bookingsData = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      console.log('My Bookings fetched:', bookingsData.length, 'bookings');
      console.log('Bookings data:', bookingsData);

      // Sort bookings by startTime in descending order (latest first)
      const sortedBookings = bookingsData.sort((a, b) => {
        return new Date(b.startTime) - new Date(a.startTime);
      });

      setBookings(sortedBookings);
      console.log('State updated with bookings');
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings', {
        position: "top-right",
        autoClose: 3000,
      });
      // For testing without backend, use mock data
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  //determine booking status based on calendar and backend call
  const getBookingStatus = (booking) => {
    // Backend status takes priority
    if (booking.status === 'CANCELLED') return 'cancelled';
    if (booking.status === 'PENDING') return 'pending';
    if (booking.status === 'REJECTED') return 'rejected';

    const now = new Date();
    const startTime = new Date(booking.startTime);
    const endTime = new Date(booking.endTime);

    // Only show upcoming bookings that are APPROVED
    if (booking.status === 'APPROVED' || booking.status === 'CONFIRMED') {
      if (endTime < now) return 'completed'; // Don't show in filters
      if (startTime > now) return 'upcoming';
      return 'ongoing';
    }

    return 'unknown';
  };


   //Filter bookings based on selected status
   // Exclude completed bookings (past bookings) from all views

  const filteredBookings = useMemo(() => {
    // Show all bookings including all recurring occurrences
    if (filterStatus === 'all') {
      // Show all active bookings (exclude completed by default)
      return bookings.filter(booking => {
        const status = getBookingStatus(booking);
        return status !== 'completed';
      });
    }

    return bookings.filter(booking => {
      const status = getBookingStatus(booking);
      return status === filterStatus;
    });
  }, [bookings, filterStatus]);

  //Formatting date and time for display
  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  //  Calculate duration in hours and minutes

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  //open confirm cancellation model
  const handleCancelClick = (booking) => {
    setBookingToCancel(booking);
    setShowCancelModal(true);
  };

  //close cancel model
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setBookingToCancel(null);
  };

  //Confirm cancellation of booking
  const confirmCancel = async () => {
    if (!bookingToCancel) return;

    try {
      setIsCancelling(true);
      await conferenceRoomApi.cancelBooking(bookingToCancel.bookingId || bookingToCancel.id);

      toast.success('Booking cancelled successfully', {
        position: "top-right",
        autoClose: 3000,
      });

      // Refresh bookings list
      await fetchMyBookings();
      closeCancelModal();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to cancel booking';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsCancelling(false);
    }
  };

 // get status badge class
  const getStatusBadgeClass = (booking) => {
    const status = getBookingStatus(booking);
    return `status-badge status-${status}`;
  };

  //Get Status label for display
  const getStatusLabel = (booking) => {
    const status = getBookingStatus(booking);
    // Show "Confirmed" for approved bookings (shorter for table layout)
    if (status === 'upcoming' && (booking.status === 'APPROVED' || booking.status === 'CONFIRMED')) {
      return 'Confirmed';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusIcon = (booking) => {
    const status = getBookingStatus(booking);
    switch (status) {
      case 'upcoming':
      case 'ongoing':
        return <MdCheckCircle />;
      case 'pending':
        return <MdPending />;
      case 'cancelled':
      case 'rejected':
        return <MdBlock />;
      default:
        return <MdBookmarkBorder />;
    }
  };

  //Check if booking can be cancelled
   //Users can cancel: pending (before approval) or upcoming approved bookings

  const canCancelBooking = (booking) => {
    const status = getBookingStatus(booking);
    return status === 'pending' || status === 'upcoming' || status === 'ongoing';
  };

  // Handle checkbox selection
  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.bookingId || b.id));
    }
  };

  // Open bulk delete modal
  const handleBulkDelete = () => {
    if (selectedBookings.length === 0) {
      toast.warning('Please select bookings to delete', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    try {
      setIsProcessing(true);
      await conferenceRoomApi.bulkDeleteBookings(selectedBookings);
      toast.success(`${selectedBookings.length} booking(s) deleted successfully`, {
        position: "top-right",
        autoClose: 3000,
      });
      setSelectedBookings([]);
      setShowBulkDeleteModal(false);
      await fetchMyBookings();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete bookings';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="admin-view my-bookings-view">
      <PageHeader breadcrumbs={["Community", "My Bookings"]} />

      {/* Combined Header and Filter Tabs */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdBookmarkBorder className="title-icon" />
            My Bookings
          </h2>
        </div>

        <div className="header-filters-compact">
          <button
            className="btn-book-compact"
            onClick={fetchMyBookings}
            disabled={isLoading}
          >
            <MdRefresh />
            <span>Refresh</span>
          </button>

          {selectedBookings.length > 0 && (
            <button
              className="btn-action-compact btn-delete"
              onClick={handleBulkDelete}
              style={{ marginLeft: '0.5rem' }}
            >
              <MdDelete />
              <span>Delete ({selectedBookings.length})</span>
            </button>
          )}
        </div>
      </div>

    {/* Filter Tabs */}
      <div className="bookings-filters">
        <button
          className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          Active
        </button>
        <button
          className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          Pending Approval
        </button>
        <button
          className={`filter-tab ${filterStatus === 'upcoming' ? 'active' : ''}`}
          onClick={() => setFilterStatus('upcoming')}
        >
          Booking Confirmed
        </button>
        <button
          className={`filter-tab ${filterStatus === 'ongoing' ? 'active' : ''}`}
          onClick={() => setFilterStatus('ongoing')}
        >
          Ongoing
        </button>
        <button
          className={`filter-tab ${filterStatus === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilterStatus('cancelled')}
        >
          Cancelled
        </button>
        <button
          className={`filter-tab ${filterStatus === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          Rejected
        </button>
        <button
          className={`filter-tab ${filterStatus === 'completed' ? 'active' : ''}`}
          onClick={() => setFilterStatus('completed')}
        >
          Completed
        </button>
      </div>

      {/* Bookings Table */}
      <div className="bookings-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner-large"></div>
            <p>Loading your bookings...</p>
          </div>
        ) : filteredBookings.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="card bookings-table-desktop">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                        onChange={handleSelectAll}
                        title="Select All"
                      />
                    </th>
                    <th style={{ width: '90px' }}>Booking ID</th>
                    <th style={{ width: '180px' }}>Room</th>
                    <th style={{ width: '170px' }}>Date & Time</th>
                    <th style={{ width: '80px' }}>Duration</th>
                    <th style={{ minWidth: '100px' }}>Purpose</th>
                    <th style={{ width: '110px' }}>Type</th>
                    <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                    <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking, idx) => {
                    const status = getBookingStatus(booking);
                    return (
                      <tr key={booking.bookingId || booking.id || idx}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedBookings.includes(booking.bookingId || booking.id)}
                            onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                          />
                        </td>
                        <td>
                          <span className="booking-id-cell" title={booking.bookingId || booking.id}>
                            {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                          </span>
                        </td>
                        <td>
                          <div className="room-info">
                            <strong>{booking.roomName || 'Workspace'}</strong>
                            <small>{booking.tower || ''} {booking.floor || ''}</small>
                          </div>
                        </td>
                        <td>
                          <div className="datetime-info">
                            <div>{formatDateTime(booking.startTime)}</div>
                          </div>
                        </td>
                        <td>
                          <span className="duration-badge">
                            {calculateDuration(booking.startTime, booking.endTime)}
                          </span>
                        </td>
                        <td>
                          <span className="purpose-text">
                            {booking.purpose || '-'}
                          </span>
                        </td>
                        <td>
                          {booking.recurringGroupId ? (
                            <div className="recurring-info">
                              <span className="recurring-badge">
                                🔁 Recurring
                              </span>
                              <div className="recurring-details">
                                {booking.recurringBookingsCount && (
                                  <span>{booking.recurringBookingsCount} bookings</span>
                                )}
                                {booking.recurringEndDate && (
                                  <div>Till: {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="single-badge">Single</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={getStatusBadgeClass(booking)}>
                            {getStatusLabel(booking)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {canCancelBooking(booking) ? (
                            <button
                              className="btn-cancel-booking-table"
                              onClick={() => handleCancelClick(booking)}
                              title="Cancel this booking"
                            >
                              <MdCancel />
                              Cancel
                            </button>
                          ) : (
                            <span className="no-action">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="bookings-cards-mobile">
              {filteredBookings.map((booking, idx) => {
                const status = getBookingStatus(booking);
                return (
                  <div className="booking-card" key={booking.bookingId || booking.id || idx}>
                    <div className="booking-card-header">
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.bookingId || booking.id)}
                        onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                        style={{ marginRight: '10px' }}
                      />
                      <div className="room-name">{booking.roomName || 'Workspace'}</div>
                      <span className={getStatusBadgeClass(booking)}>
                        {getStatusLabel(booking)}
                      </span>
                    </div>
                    <div className="booking-card-body">
                      <div className="booking-detail">
                        <span className="label">Booking ID:</span>
                        <span className="value booking-id-cell" title={booking.bookingId || booking.id}>
                          {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                        </span>
                      </div>
                      <div className="booking-detail">
                        <span className="label"><MdLocationOn style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Location:</span>
                        <span className="value">{booking.tower || ''} {booking.floor || ''}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="label"><MdCalendarToday style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Date & Time:</span>
                        <span className="value">{formatDateTime(booking.startTime)}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="label"><MdAccessTime style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Duration:</span>
                        <span className="value duration-badge">
                          {calculateDuration(booking.startTime, booking.endTime)}
                        </span>
                      </div>
                      <div className="booking-detail">
                        <span className="label">📝 Purpose:</span>
                        <span className="value">{booking.purpose || '-'}</span>
                      </div>
                      {booking.recurringGroupId ? (
                        <div className="booking-detail recurring-detail">
                          <div className="recurring-mobile-info">
                            <strong>🔁 Recurring Series</strong>
                            <div className="recurring-mobile-details">
                              {booking.recurringBookingsCount && (
                                <span>{booking.recurringBookingsCount} bookings in series</span>
                              )}
                              {booking.recurringEndDate && (
                                <span>Till: {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="booking-detail">
                          <span className="label">Type:</span>
                          <span className="value">Single Booking</span>
                        </div>
                      )}
                      {canCancelBooking(booking) && (
                        <div className="booking-card-actions">
                          <button
                            className="btn-cancel-booking"
                            onClick={() => handleCancelClick(booking)}
                          >
                            <MdCancel />
                            Cancel Booking
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="empty-state card">
            <div className="empty-icon">📅</div>
            <h3>No {filterStatus !== 'all' ? filterStatus : ''} bookings found</h3>
            <p>
              {filterStatus === 'pending' && "You don't have any bookings awaiting approval."}
              {filterStatus === 'upcoming' && "You don't have any confirmed bookings."}
              {filterStatus === 'ongoing' && "You don't have any ongoing bookings."}
              {filterStatus === 'cancelled' && "You don't have any cancelled bookings."}
              {filterStatus === 'rejected' && "You don't have any rejected bookings."}
              {filterStatus === 'completed' && "You don't have any completed bookings."}
              {filterStatus === 'all' && "You haven't made any active bookings yet."}
            </p>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={closeCancelModal}>
          <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Booking</h3>
              <button className="modal-close" onClick={closeCancelModal}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this booking?</p>
              {bookingToCancel && (
                <div className="booking-summary">
                  <div><strong>Workspace:</strong> {bookingToCancel.roomName || 'Workspace'}</div>
                  <div><strong>Date:</strong> {formatDateTime(bookingToCancel.startTime)}</div>
                  <div><strong>Duration:</strong> {calculateDuration(bookingToCancel.startTime, bookingToCancel.endTime)}</div>
                  {bookingToCancel.recurringGroupId && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f4fd', borderRadius: '4px' }}>
                      <strong>🔁 Recurring Booking Series</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                        Note: This will only cancel this specific booking, not the entire series.
                      </p>
                    </div>
                  )}
                </div>
              )}
              <p className="warning-text">⚠️ This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={closeCancelModal}
                disabled={isCancelling}
              >
                Keep Booking
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={confirmCancel}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Multiple Bookings</h3>
              <button className="modal-close" onClick={() => setShowBulkDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {selectedBookings.length} booking(s)?</p>
              <div className="expired-booking-warning" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                <span className="warning-icon">⚠️</span>
                <div className="warning-content">
                  <strong>Warning!</strong>
                  <p>This action cannot be undone. All selected bookings will be permanently deleted.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={confirmBulkDelete}
                disabled={isProcessing}
              >
                {isProcessing ? 'Deleting...' : `Yes, Delete ${selectedBookings.length} Booking(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyBookings;
